
import { ShoppingItem, UserProduct, ShoppingItemWithProduct } from "@/types";
import { offlineDb, SyncableShoppingItem, SyncableUserProduct } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';
import { syncService } from '@/services/sync.service';
import { queryClient } from '@/services/queryClient';
import { v4 as uuid } from 'uuid';
import { validateQuantity } from '@/utils/security';

const DEFAULT_SANITIZED_QUANTITY = (() => {
  const result = validateQuantity('1');
  return result.isValid ? result.sanitized : '1';
})();

const normalizeQuantityValue = (value?: string | number | null): string => {
  const result = validateQuantity(value ?? DEFAULT_SANITIZED_QUANTITY);
  if (result.isValid) {
    return result.sanitized;
  }
  return DEFAULT_SANITIZED_QUANTITY;
};

// Helper functions to convert between ShoppingItem and SyncableShoppingItem
const convertToSyncableItem = (item: ShoppingItem): SyncableShoppingItem => ({
  id: item.id,
  list_id: item.list_id,
  product_id: item.product_id,
  quantity: normalizeQuantityValue(item.quantity),
  unit: item.unit,
  // #1421: la categoria "vuota" si normalizza a null nello storage Dexie, così
  // coincide con la rappresentazione su Supabase (null) ed evita falsi "modificati"
  // nei confronti di sync. La UI continua a leggere '' via convertToShoppingItem.
  category: item.category || null,
  checked: item.checked || false,
  position: item.position,
  attributes: item.attributes || [],
  created_at: item.created_at instanceof Date ? item.created_at.toISOString() : (typeof item.created_at === 'string' ? item.created_at : new Date().toISOString()),
  user_id: item.user_id, 
  _syncStatus: 'pending' as const,
  _lastModified: Date.now()
});

const convertToShoppingItem = (syncableItem: SyncableShoppingItem): ShoppingItem => ({
  id: syncableItem.id,
  list_id: syncableItem.list_id,
  product_id: syncableItem.product_id,
  quantity: normalizeQuantityValue(syncableItem.quantity),
  unit: syncableItem.unit || "",
  category: syncableItem.category || "",
  checked: syncableItem.checked === true,
  position: syncableItem.position,
  attributes: syncableItem.attributes || [],
  created_at: new Date(syncableItem.created_at || Date.now()),
  user_id: syncableItem.user_id
});

const convertToUserProductLocal = (p: SyncableUserProduct): UserProduct => ({
  id: p.id,
  userId: p.user_id,
  name: p.name,
  defaultQuantity: p.default_quantity,
  defaultUnit: p.default_unit,
  photo: p.photo,
  barcode: p.barcode,
  openfoodfacts_id: p.openfoodfacts_id,
  categoryId: p.category_id,
  createdAt: p.created_at,
  attributes: p.attributes || [],
});

export class ShoppingItemRepository {
  protected tableName = 'shopping_items';
  protected dbTable = offlineDb.shoppingItems;
  
  async create(item: ShoppingItem, listId: string): Promise<ShoppingItem> {
    try {
      const id = item.id || uuid();
      const newItem: ShoppingItem = {
        ...item,
        id
      };
      
      const syncableItem = convertToSyncableItem(newItem);
      await this.dbTable.put(syncableItem);
      
      // ✅ FIX: Pulisci i dati prima di aggiungerli alla coda di sync
      const cleanDataForSync = this.cleanDataForSync(syncableItem);
      
      // Add to sync queue for online sync later
      await syncService.addToSyncQueue(
        'shoppingItems',
        'create',
        id,
        cleanDataForSync
      );

      OfflineLogger.info('ShoppingItemRepository.create', `Created item: ${id}`);

      // Notify listeners that we've added an item
      this.dispatchSyncEvent();

      await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
      await queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
      await queryClient.invalidateQueries({ queryKey: ['enhanced-primary-list'] });

      return newItem;
    } catch (error) {
      OfflineLogger.error('ShoppingItemRepository.create', error);
      throw error;
    }
  }

  // ✅ FIX: Nuovo metodo per pulire i dati prima della sincronizzazione
  private cleanDataForSync(syncableItem: SyncableShoppingItem): any {
    const cleanData = { ...syncableItem };

    // Rimuovi i campi di sincronizzazione
    delete cleanData._syncStatus;
    delete cleanData._lastModified;

    // Converti le stringhe vuote in null prima della sincronizzazione
    Object.keys(cleanData).forEach(key => {
      if (cleanData[key] === '') {
        cleanData[key] = null;
      }
    });

    if ('quantity' in cleanData && cleanData.quantity !== null) {
      cleanData.quantity = normalizeQuantityValue(cleanData.quantity as string | number | null);
    }

    return cleanData;
  }

  async saveMany(items: ShoppingItem[] | SyncableShoppingItem[]): Promise<void> {
    try {
      const toWrite: SyncableShoppingItem[] = [];
      for (const item of items) {
        // Items without _syncStatus come from the server: mark as synced (not pending).
        // Items that already carry _syncStatus (e.g. SyncableShoppingItem) keep their own status.
        const syncable: SyncableShoppingItem = '_syncStatus' in item
          ? item as SyncableShoppingItem
          : { ...convertToSyncableItem(item as ShoppingItem), _syncStatus: 'synced' as const };

        // pending-local-wins: non sovrascrivere un edit locale non ancora sincronizzato
        const existing = await this.dbTable.get(syncable.id);
        if (existing && (existing._syncStatus === 'pending' || existing._syncStatus === 'deleted')) {
          continue;
        }
        toWrite.push(syncable);
      }
      if (toWrite.length > 0) {
        await this.dbTable.bulkPut(toWrite);
      }
      OfflineLogger.info('ShoppingItemRepository.saveMany', `Saved ${toWrite.length} items to local DB`);
    } catch (error) {
      OfflineLogger.error('ShoppingItemRepository.saveMany', error);
      throw error;
    }
  }

  async getByListId(listId: string): Promise<ShoppingItem[]> {
    try {
      const items = await this.dbTable
        .where('list_id')
        .equals(listId)
        .and(item => item._syncStatus !== 'deleted')
        .toArray();
        
      return items.map(convertToShoppingItem);
    } catch (error) {
      OfflineLogger.error('ShoppingItemRepository.getByListId', error);
      return [];
    }
  }

  async getWithProductsByListId(listId: string): Promise<ShoppingItemWithProduct[]> {
    try {
      const items = await this.dbTable
        .where("list_id")
        .equals(listId)
        .and(item => item._syncStatus !== "deleted")
        .toArray();
      const results: ShoppingItemWithProduct[] = [];
      for (const item of items) {
        const productData = item.product_id ? await offlineDb.products.get(item.product_id) : null;
        results.push({
          ...convertToShoppingItem(item),
          name: productData?.name ?? '',
          product: productData ? convertToUserProductLocal(productData as SyncableUserProduct) : undefined
        } as ShoppingItemWithProduct);
      }
      return results;
    } catch (error) {
      OfflineLogger.error("ShoppingItemRepository.getWithProductsByListId", error);
      return [];
    }
  }
  
  async update(id: string, updates: Partial<ShoppingItem>): Promise<ShoppingItem | null> {
    try {
      // Construct update object with proper typing
      const updateData: Partial<SyncableShoppingItem> = {};
      
      // Copy valid fields from updates, excluding id and list_id
      Object.entries(updates).forEach(([key, value]) => {
        if (key !== 'id' && key !== 'list_id') {
          if (key === 'quantity') {
            (updateData as any)[key] = normalizeQuantityValue(value as string | number | null);
          } else {
            (updateData as any)[key] = value;
          }
        }
      });
      
      // Add sync metadata
      updateData._syncStatus = 'pending';
      updateData._lastModified = Date.now();
      
      // Update in local DB
      await this.dbTable.update(id, updateData);
      
      // Get the updated item
      const updated = await this.dbTable.get(id);
      if (!updated) return null;
      
      // ✅ FIX: Pulisci i dati prima di aggiungerli alla coda di sync
      const cleanDataForSync = this.cleanDataForSync({
        ...updateData,
        id,
        list_id: updated.list_id,
        product_id: updated.product_id,
        created_at: updated.created_at,
        user_id: updated.user_id
      } as SyncableShoppingItem);
      
      // Add to sync queue
      await syncService.addToSyncQueue('shoppingItems', 'update', id, cleanDataForSync);

      this.dispatchSyncEvent();

      // Invalida le query correlate per aggiornare UI e contatori
      await queryClient.invalidateQueries({ queryKey: ['shopping-items', updated.list_id] });
      await queryClient.invalidateQueries({ queryKey: ['shopping-list', updated.list_id] });
      await queryClient.invalidateQueries({ queryKey: ['enhanced-primary-list'] });

      return convertToShoppingItem(updated);
    } catch (error) {
      OfflineLogger.error('ShoppingItemRepository.update', error);
      throw error;
    }
  }
  
  async updateItemStatus(id: string, checked: boolean): Promise<boolean> {
    try {
      const existing = await this.dbTable.get(id);
      const normalizedQuantity = normalizeQuantityValue(existing?.quantity ?? null);

      // Update locally
      await this.dbTable.update(id, {
        checked,
        quantity: normalizedQuantity,
        _syncStatus: 'pending' as const,
        _lastModified: Date.now(),
      });

      // Add to sync queue with clean data
      await syncService.addToSyncQueue('shoppingItems', 'update', id, {
        checked,
        quantity: normalizedQuantity
      });

      this.dispatchSyncEvent();

      return true;
    } catch (error) {
      OfflineLogger.error('ShoppingItemRepository.updateItemStatus', error);
      return false;
    }
  }
  
  async delete(id: string): Promise<void> {
    try {
      // Mark as deleted in local DB
      await this.dbTable.update(id, {
        _syncStatus: 'deleted' as const,
        _lastModified: Date.now()
      });
      
      // Add to sync queue
      await syncService.addToSyncQueue('shoppingItems', 'delete', id);

      this.dispatchSyncEvent();

      OfflineLogger.info('ShoppingItemRepository.delete', `Marked item ${id} for deletion`);
    } catch (error) {
      OfflineLogger.error('ShoppingItemRepository.delete', error);
      throw error;
    }
  }
  
  private dispatchSyncEvent(): void {
    // Notify that there are pending operations
    syncService.getPendingOperationsCount().then(count => {
      window.dispatchEvent(
        new CustomEvent('syncStatusChanged', {
          detail: { pendingOperations: count }
        })
      );
    });
  }
}

export const shoppingItemRepository = new ShoppingItemRepository();
